import { ZodLessInferInput, ZodLessInferOutput, zodLessParse, ZodLessSchema, ZodTypeAny } from '@kaiizen/zod-less';
import { createInvalidRequestError } from './ErrorWithStatus';
import { fetchJson, FetchUploadableBinaryData, getBinary, postFormDataJsonResponseParsed } from './FetchLib';
import { urlJoin } from './stringLib';
import { HttpMethodUppercase } from './types';
import { EndPoint } from './zodLessEndpoints';

type RequestOptions = {
  apiUrl?: string;
  verbose?: boolean;
  token?: string;
  errorHandler?: (error: any, info?: any) => void;
};

const CLIENT_SIDE_VALIDATION_ENABLED = false;

const validateInput = (input: any, inputSchema: ZodLessSchema, method: HttpMethodUppercase, path: string) => {
  if (!CLIENT_SIDE_VALIDATION_ENABLED) return;
  if (!input.query) input = { ...input, query: {} }; // if query is in inputSchema, it can't be optional or else openapi generation breaks
  try {
    zodLessParse(inputSchema as any, input, `Client-side input validation for ${method} ${path} request`); // validate input
  } catch (error: any) {
    throw createInvalidRequestError(error.message || 'Client-side input validation failed', null, { input });
  }
};

export class ZodLessApiClient {
  /**
   * The token pass in with the options for the ZodLessApiClient.
   * @returns The token.
   */
  get token() {
    return this.options.token;
  }

  /**
   * The options for the ZodLessApiClient:
   * - apiUrl: The base url for the ZodLessApi.
   * - verbose: Whether to log the request and response.
   * - token: The token to pass in with the request. (in the bearer header)
   * - errorHandler: The error handler to use for the ZodLessApiClient.
   * @returns The options.
   */
  constructor(public options: RequestOptions = {}) {}

  /**
   * Provide a default error handler for the ZodLessApiClient.
   * @returns The error handler.
   */
  get errorHandler() {
    return async (e: any, info: any) => {
      this.options?.errorHandler && await this.options.errorHandler(e, info);
      throw e;
    };
  }

  /**
   * Make a JSON request to a ZodLessApi Endpoint. Any pure json request should use this method.
   * @param endPoint - The ZodLessApi Endpoint.
   * @param input - The input to the Endpoint.
   * @returns The response from the Endpoint.
   */
  jsonRequest<In extends ZodTypeAny, Out extends ZodTypeAny>(endPoint: EndPoint<In, Out>, input: ZodLessInferInput<In>, timeoutSeconds?: number) {
    validateInput(input, endPoint.input, endPoint.method, endPoint.path);
    const url = urlJoin(this.options?.apiUrl, endPoint.path);
    return fetchJson(
      endPoint.method,
      url,
      { bearer: this.options.token, ...input },
      false,
      this.options?.verbose,
      timeoutSeconds,
    ).then(json => CLIENT_SIDE_VALIDATION_ENABLED ? zodLessParse(endPoint.output, json) : json as ZodLessInferOutput<Out>)
      .catch(error => this.errorHandler(error, { endPoint, input }));
  }

  /**
   * For Binary Downloads
   * Make a json-in, binary-out request to a ZodLessApi Endpoint. Any binary request should use this method.
   * @param endPoint - The ZodLessApi Endpoint.
   * @param input - The input to the Endpoint.
   * @returns The response from the Endpoint.
   */
  getBinary<In extends ZodTypeAny>(endPoint: EndPoint<In, ZodTypeAny>, input: ZodLessInferInput<typeof endPoint.input>) {
    validateInput(input, endPoint.input, endPoint.method, endPoint.path);
    const url = urlJoin(this.options?.apiUrl, endPoint.path);
    return getBinary(url, { bearer: this.options.token, ...input })
      .catch(error => this.errorHandler(error, { endPoint, input })) as Promise<Blob>;
  }

  /**
   * For Binary Uploads
   * Uses FormData to send simple key-value pairs and a binary file to a ZodLessApi Endpoint.
   * @param endPoint - The ZodLessApi Endpoint.
   * @param input - The input to the Endpoint.
   * @returns The response from the Endpoint.
   */
  postFile<In extends ZodTypeAny, Out extends ZodTypeAny>(
    endPoint: EndPoint<In, Out>,
    input: ZodLessInferInput<In>,
    file: FetchUploadableBinaryData,
    timeoutSeconds?: number,
    onProgress?: (progress: number) => void,
  ) {
    validateInput(input, endPoint.input, endPoint.method, endPoint.path);
    const url = urlJoin(this.options?.apiUrl, endPoint.path);
    return postFormDataJsonResponseParsed(url, { bearer: this.options.token, ...input, files: { file } } as any, endPoint.output, timeoutSeconds, onProgress)
      .then(json => CLIENT_SIDE_VALIDATION_ENABLED ? zodLessParse(endPoint.output, json) : json as ZodLessInferOutput<Out>)
      .catch(error => this.errorHandler(error, { endPoint, input }));
  }
}
